'use client';

import React from 'react';
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@/primitives';
import { AnimalSexBadge } from '@/hooks/publicSearch/animal/helpers';
import { ANIMAL_SEX } from '@/components/animals/animalSex';
import MatingInbreedingLine from '../MatingInbreedingLine';
import {
  PRIMARY_BELOW_GAP_PX,
  STICKY_PRIMARY_ROW_BORDER,
  TABLE_SCROLL_BOTTOM_SPACING_PX,
  Z_MATE_STICKY_NAME,
  Z_PRIMARY_NAME,
} from './constants';
import MatingTableHead from './MatingTableHead';
import BreedAverageRow from './BreedAverageRow';
import { MATING_DISPLAY_BY } from '../types';
import { useMatingResultsUrls } from '../matingResultsUrls';
import { useDesktopMatingTableLayout } from './useDesktopMatingTableLayout';
import {
  renderEbvCells,
  renderProjectedEbvCells,
} from './DesktopMatingTableEbvCells';
import type { DesktopMatingTableProps } from './desktopMatingTableTypes';

export type { DesktopMatingTableProps };

export default function DesktopMatingTable({
  data,
  ebvGroups,
  visibleEbvs,
  groupStartFields,
  ebvAverage,
  currencySymbol,
  societyId,
  chrome,
  traitHeaderContext,
  displayBy,
  currentEntry,
  currentPageIndex,
  safePageIndex,
  primaryGroupId,
  totalPages,
  sireMap,
  damMap,
  animalUrl,
  onInbreedingInfoClick,
  ebvGroupingOptions,
}: DesktopMatingTableProps) {
  const { buildPedigreeHref } = useMatingResultsUrls();

  const {
    theadRef,
    categoryHeaderRowRef,
    breedAveragesRowRef,
    tableContainerRef,
    primaryRowRef,
    stickyHeaderHeight,
    categoryHeaderHeight,
    primaryStickyTop,
    tableScrollClientWidth,
    onResultsScroll,
  } = useDesktopMatingTableLayout({
    data,
    ebvGroups,
    visibleEbvs,
    ebvAverage,
    displayBy,
    currentPageIndex,
    safePageIndex,
    primaryGroupId,
    totalPages,
  });

  return (
    <TableContainer
      ref={tableContainerRef}
      onScroll={onResultsScroll}
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        position: 'relative',
        paddingBottom: `${TABLE_SCROLL_BOTTOM_SPACING_PX}px`,
      }}
    >
      <Table
        size="small"
        stickyHeader
        sx={{
          borderCollapse: 'separate',
          borderSpacing: 0,
          '& thead': { borderBottom: 0 },
          '& tbody': { borderTop: 0 },
          // Strip theme cell borders on vertical-gap rows between mate blocks.
          '& tbody tr[data-mating-vertical-gap] .MuiTableCell-root': {
            border: 'none',
            borderWidth: 0,
            borderStyle: 'hidden',
            borderTop: 'none',
            borderRight: 'none',
            borderBottom: 'none',
            borderLeft: 'none',
            boxShadow: 'none',
          },
        }}
      >
        <MatingTableHead
          theadRef={theadRef}
          categoryHeaderRowRef={categoryHeaderRowRef}
          ebvGroups={ebvGroups}
          visibleEbvs={visibleEbvs}
          groupStartFields={groupStartFields}
          chrome={chrome}
          categoryHeaderHeight={categoryHeaderHeight}
          traitHeaderContext={traitHeaderContext}
          ebvGroupingOptions={ebvGroupingOptions}
        />
        <TableBody>
          {/* Breed Averages row – always shown when society has EBV_AVERAGE config */}
          {ebvAverage && (
            <BreedAverageRow
              breedAveragesRowRef={breedAveragesRowRef}
              ebvAverage={ebvAverage}
              visibleEbvs={visibleEbvs}
              groupStartFields={groupStartFields}
              chrome={chrome}
              stickyHeaderHeight={stickyHeaderHeight}
              currencySymbol={currencySymbol}
              societyId={societyId}
              ebvGroupingOptions={ebvGroupingOptions}
            />
          )}

          {!currentEntry ? (
            <TableRow>
              <TableCell colSpan={visibleEbvs.length + 1}>
                <Typography
                  variant="bodymedium"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  No prediction results to display.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            (() => {
              const [primaryId, pairs] = currentEntry;
              const primaryAnimal =
                displayBy === MATING_DISPLAY_BY.DAM
                  ? damMap.get(primaryId)
                  : sireMap.get(primaryId);
              const primaryLabel =
                displayBy === MATING_DISPLAY_BY.DAM ? 'DAM' : 'SIRE';

              if (!primaryAnimal) {
                return (
                  <TableRow>
                    <TableCell colSpan={visibleEbvs.length + 1}>
                      <Typography
                        variant="bodymedium"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        No prediction results to display.
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <React.Fragment key={primaryId}>
                  <TableRow
                    ref={primaryRowRef}
                    sx={{
                      '& > .MuiTableCell-root': {
                        borderRadius: 0,
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        top: primaryStickyTop,
                        zIndex: Z_PRIMARY_NAME,
                        minWidth: 200,
                        maxWidth: 800,
                        color: 'text.primary',
                        backgroundColor: chrome.primaryRowBaseBg,
                        borderBottom: STICKY_PRIMARY_ROW_BORDER,
                        borderBottomColor: chrome.primaryRowBorderColor,
                        py: 1,
                        whiteSpace: 'nowrap',
                        boxShadow: chrome.stickyColShadow,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          whiteSpace: 'nowrap',
                          fontWeight: 700,
                        }}
                      >
                        <AnimalSexBadge
                          sex={
                            displayBy === MATING_DISPLAY_BY.DAM
                              ? ANIMAL_SEX.FEMALE
                              : ANIMAL_SEX.MALE
                          }
                        />
                        <Typography
                          component="span"
                          variant="bodymedium"
                          sx={{
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            fontSize: '0.8125rem',
                          }}
                        >
                          {primaryLabel}
                        </Typography>
                        <Link
                          href={animalUrl(primaryAnimal.animalId)}
                          sx={{
                            color: 'inherit',
                            textDecoration: 'none',
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {primaryAnimal.animalName} -{' '}
                          {primaryAnimal.animalIdent}
                        </Link>
                      </Box>
                    </TableCell>
                    {renderEbvCells({
                      animal: primaryAnimal,
                      configs: visibleEbvs,
                      chrome,
                      currencySymbol,
                      groupStartFields,
                      primaryStickyTop,
                      ebvGroupingOptions,
                      options: { primaryRow: true },
                    })}
                  </TableRow>

                  <TableRow data-mating-vertical-gap>
                    <TableCell
                      colSpan={visibleEbvs.length + 1}
                      sx={{
                        py: 0,
                        height: PRIMARY_BELOW_GAP_PX,
                        backgroundColor: 'background.default',
                      }}
                    />
                  </TableRow>

                  {pairs.map((result, pairIndex) => {
                    const secondaryId =
                      displayBy === MATING_DISPLAY_BY.DAM
                        ? result.sireId
                        : result.damId;
                    const secondaryAnimal =
                      displayBy === MATING_DISPLAY_BY.DAM
                        ? sireMap.get(secondaryId)
                        : damMap.get(secondaryId);
                    const secondaryLabel =
                      displayBy === MATING_DISPLAY_BY.DAM ? 'SIRE' : 'DAM';

                    if (!secondaryAnimal) return null;

                    const sire =
                      displayBy === MATING_DISPLAY_BY.DAM
                        ? secondaryAnimal
                        : primaryAnimal;
                    const dam =
                      displayBy === MATING_DISPLAY_BY.DAM
                        ? primaryAnimal
                        : secondaryAnimal;

                    return (
                      <React.Fragment key={`${result.sireId}-${result.damId}`}>
                        {pairIndex > 0 && (
                          <TableRow data-mating-vertical-gap>
                            <TableCell
                              colSpan={visibleEbvs.length + 1}
                              sx={{
                                py: 0,
                                height: 16,
                                backgroundColor: 'background.default',
                              }}
                            />
                          </TableRow>
                        )}

                        <TableRow
                          data-snap-point
                          sx={{
                            '& > .MuiTableCell-root': {
                              borderBottom: chrome.cellBorder,
                              borderTop: chrome.pairGroupBorder,
                            },
                            '& > .MuiTableCell-root:not(:first-of-type)': {
                              position: 'relative',
                              zIndex: 0,
                            },
                            '& > .MuiTableCell-root:first-of-type': {
                              borderLeft: chrome.pairGroupBorder,
                            },
                            '& > .MuiTableCell-root:last-of-type': {
                              borderRight: chrome.pairGroupBorder,
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: Z_MATE_STICKY_NAME,
                              minWidth: 200,
                              maxWidth: 800,
                              color: 'text.primary',
                              backgroundColor: 'background.paper',
                              py: 1,
                              whiteSpace: 'nowrap',
                              boxShadow: chrome.stickyColShadow,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <AnimalSexBadge
                                sex={
                                  displayBy === MATING_DISPLAY_BY.DAM
                                    ? ANIMAL_SEX.MALE
                                    : ANIMAL_SEX.FEMALE
                                }
                              />
                              <Typography
                                component="span"
                                variant="bodymedium"
                                sx={{
                                  fontWeight: 700,
                                  textTransform: 'uppercase',
                                  fontSize: '0.8125rem',
                                }}
                              >
                                {secondaryLabel}
                              </Typography>
                              <Link
                                href={animalUrl(secondaryAnimal.animalId)}
                                sx={{
                                  color: 'inherit',
                                  textDecoration: 'none',
                                  fontSize: '0.8125rem',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                  },
                                }}
                              >
                                {secondaryAnimal.animalName} -{' '}
                                {secondaryAnimal.animalIdent}
                              </Link>
                            </Box>
                          </TableCell>
                          {renderEbvCells({
                            animal: secondaryAnimal,
                            configs: visibleEbvs,
                            chrome,
                            currencySymbol,
                            groupStartFields,
                            primaryStickyTop,
                            ebvGroupingOptions,
                          })}
                        </TableRow>

                        <TableRow
                          sx={{
                            '& > .MuiTableCell-root': {
                              borderBottom: result.inbreeding
                                ? chrome.cellBorder
                                : chrome.pairGroupBorder,
                            },
                            '& > .MuiTableCell-root:not(:first-of-type)': {
                              position: 'relative',
                              zIndex: 0,
                            },
                            '& > .MuiTableCell-root:first-of-type': {
                              borderLeft: chrome.pairGroupBorder,
                            },
                            '& > .MuiTableCell-root:last-of-type': {
                              borderRight: chrome.pairGroupBorder,
                            },
                          }}
                        >
                          <TableCell
                            sx={{
                              position: 'sticky',
                              left: 0,
                              zIndex: Z_MATE_STICKY_NAME,
                              minWidth: 200,
                              maxWidth: 800,
                              color: 'text.primary',
                              backgroundColor: chrome.epaRowBg,
                              py: 1,
                              fontSize: '0.8125rem',
                              boxShadow: chrome.stickyColShadow,
                            }}
                          >
                            Expected Progeny Average
                          </TableCell>
                          {renderProjectedEbvCells({
                            result,
                            configs: visibleEbvs,
                            chrome,
                            currencySymbol,
                            groupStartFields,
                            ebvGroupingOptions,
                          })}
                        </TableRow>

                        {result.inbreeding && (
                          <TableRow>
                            <TableCell
                              colSpan={visibleEbvs.length + 1}
                              sx={{
                                p: 0,
                                borderLeft: 'none',
                                borderRight: 'none',
                                borderBottom: chrome.pairGroupBorder,
                                backgroundColor: 'background.paper',
                              }}
                            >
                              <Box
                                sx={{
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: Z_MATE_STICKY_NAME,
                                  boxSizing: 'border-box',
                                  width:
                                    tableScrollClientWidth > 0
                                      ? `${tableScrollClientWidth}px`
                                      : '100%',
                                  minHeight: '100%',
                                  borderLeft: chrome.pairGroupBorder,
                                  borderRight: chrome.pairGroupBorder,
                                  backgroundColor: 'background.paper',
                                  py: 0.5,
                                  px: 1,
                                  boxShadow: chrome.stickyColShadow,
                                }}
                              >
                                <MatingInbreedingLine
                                  inbreeding={result.inbreeding}
                                  pedigreeHref={buildPedigreeHref(
                                    sire.animalId,
                                    dam.animalId,
                                  )}
                                  onInfoClick={onInbreedingInfoClick}
                                />
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              );
            })()
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
